import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Logger } from "aws-amplify";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Modal from "react-bootstrap/Modal";
import ProgressBar from "react-bootstrap/ProgressBar";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Container from "../components/Container";
import { encodeWavFromAudioBuffer } from "../libs/wavEncode";
import config from "../config";

const logger = new Logger("VocoderPublic", "DEBUG");

function toDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

async function decodeToAudioBuffer(arrayBuffer) {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  const ctx = new AudioCtx();
  try {
    const audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
    return audioBuffer;
  } finally {
    // decodeAudioData doesn't require the context to remain open
    if (typeof ctx.close === "function") {
      ctx.close();
    }
  }
}

function nowTs() {
  return Math.floor(Date.now() / 1000);
}

const MAX_RECORD_SECONDS = 15;

export default function VocoderPublic() {
  const [tokenInput, setTokenInput] = useState("");
  // Token is session-only (relocks on refresh)
  const [token, setToken] = useState("");
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [files, setFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [cleanupMsg, setCleanupMsg] = useState(null);
  const [isCleaning, setIsCleaning] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearConfirm, setClearConfirm] = useState("");
  const [isClearing, setIsClearing] = useState(false);

  const [processingPct, setProcessingPct] = useState(0);

  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isStoppingRecording, setIsStoppingRecording] = useState(false);
  const [recordingRemaining, setRecordingRemaining] = useState(MAX_RECORD_SECONDS);
  const recordingTimerRef = useRef(null);
  const processingTimerRef = useRef(null);

  const apiBaseUrl = config.apiGateway.URL;

  const authHeaders = useMemo(() => {
    if (!token) return {};
    // Primary auth: Bearer token (backend also accepts Basic for compatibility)
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const apiFetch = useCallback(
    async (path, init = {}) => {
      const url = `${apiBaseUrl}${path}`;
      const resp = await fetch(url, {
        ...init,
        headers: {
          ...(init.headers || {}),
          ...authHeaders,
        },
      });
      const text = await resp.text();
      let data;
      try {
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        data = { raw: text };
      }
      if (!resp.ok) {
        if (resp.status === 401) {
          throw new Error("Unauthorized (bad token)");
        }
        const msg = (data && (data.error || data.message)) || resp.statusText;
        throw new Error(msg || `Request failed (${resp.status})`);
      }
      return data;
    },
    [apiBaseUrl, authHeaders]
  );

  const refreshList = useCallback(async () => {
    try {
      const resp = await apiFetch("/vocoder/files");
      setFiles(resp.files || []);
    } catch (e) {
      logger.debug(e);
      // If we can't list, treat as auth failure and lock the UI.
      setAuthError(e.message || "Failed to load files");
      setIsUnlocked(false);
    }
  }, [apiFetch]);

  const cleanupOld = useCallback(async () => {
    setCleanupMsg(null);
    setError(null);
    setAuthError(null);
    setIsCleaning(true);
    try {
      const resp = await apiFetch("/vocoder/cleanup", { method: "POST" });
      setCleanupMsg(`Deleted ${resp.deleted || 0} old file(s).`);
      await refreshList();
    } catch (e) {
      logger.debug(e);
      if ((e.message || "").toLowerCase().includes("unauthorized")) {
        setAuthError("Token rejected. Please unlock again.");
        setIsUnlocked(false);
      } else {
        setError(e.message || "Cleanup failed");
      }
    } finally {
      setIsCleaning(false);
    }
  }, [apiFetch, refreshList]);

  const clearAll = useCallback(async () => {
    setCleanupMsg(null);
    setError(null);
    setAuthError(null);
    setIsClearing(true);
    try {
      const resp = await apiFetch("/vocoder/clear", { method: "POST" });
      setCleanupMsg(`Deleted ${resp.deleted || 0} file(s).`);
      await refreshList();
      setShowClearModal(false);
      setClearConfirm("");
    } catch (e) {
      logger.debug(e);
      if ((e.message || "").toLowerCase().includes("unauthorized")) {
        setAuthError("Token rejected. Please unlock again.");
        setIsUnlocked(false);
      } else {
        setError(e.message || "Delete all failed");
      }
    } finally {
      setIsClearing(false);
    }
  }, [apiFetch, refreshList]);

  useEffect(() => {
    if (!isUnlocked) return;
    refreshList();
    const id = setInterval(refreshList, 15000);
    return () => clearInterval(id);
  }, [isUnlocked, refreshList]);

  async function processWavBlob(wavBlob) {
    setError(null);
    setAuthError(null);
    setIsProcessing(true);
    setProcessingPct(0);

    if (processingTimerRef.current) clearInterval(processingTimerRef.current);
    // Simulated progress while server is working (no real progress from backend)
    processingTimerRef.current = setInterval(() => {
      setProcessingPct((p) => {
        if (p >= 95) return 95;
        const next = p + Math.max(1, Math.round((95 - p) * 0.08));
        return Math.min(95, next);
      });
    }, 350);

    try {
      const audioBase64 = await toDataUrl(wavBlob);
      const resp = await apiFetch("/vocoder", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ audioBase64, maxSeconds: 12 }),
      });
      setLastResult(resp || null);
      await refreshList();
      setProcessingPct(100);
    } catch (e) {
      logger.debug(e);
      if ((e.message || "").toLowerCase().includes("unauthorized")) {
        setAuthError("Token rejected. Please unlock again.");
        setIsUnlocked(false);
      } else {
        setError(e.message || "Processing failed");
      }
    } finally {
      setIsProcessing(false);
      if (processingTimerRef.current) clearInterval(processingTimerRef.current);
      processingTimerRef.current = null;
    }
  }

  async function handleUploadFile(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setError(null);

    try {
      const ab = await file.arrayBuffer();
      const audioBuffer = await decodeToAudioBuffer(ab);
      const wavBlob = encodeWavFromAudioBuffer(audioBuffer);
      await processWavBlob(wavBlob);
    } catch (err) {
      logger.debug(err);
      setError(
        "Could not decode that file. Try a short WAV or MP3 (Chrome works best)."
      );
    } finally {
      e.target.value = "";
    }
  }

  async function startRecording() {
    setError(null);
    setAuthError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      recordedChunksRef.current = [];

      rec.ondataavailable = (evt) => {
        if (evt.data && evt.data.size > 0) recordedChunksRef.current.push(evt.data);
      };

      rec.onstop = async () => {
        try {
          const blob = new Blob(recordedChunksRef.current);
          const ab = await blob.arrayBuffer();
          const audioBuffer = await decodeToAudioBuffer(ab);
          const wavBlob = encodeWavFromAudioBuffer(audioBuffer);
          await processWavBlob(wavBlob);
        } catch (err) {
          logger.debug(err);
          setError(
            "Recording captured, but decoding failed. Try again (Chrome works best)."
          );
        } finally {
          stream.getTracks().forEach((t) => t.stop());
          setIsRecording(false);
          setIsStoppingRecording(false);
          setRecordingRemaining(MAX_RECORD_SECONDS);
          if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
      };

      mediaRecorderRef.current = rec;
      rec.start();
      setIsRecording(true);
      setIsStoppingRecording(false);

      // Countdown + auto-stop at MAX_RECORD_SECONDS
      const startedAt = Date.now();
      setRecordingRemaining(MAX_RECORD_SECONDS);
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = setInterval(() => {
        const elapsed = (Date.now() - startedAt) / 1000;
        const remaining = Math.max(0, MAX_RECORD_SECONDS - elapsed);
        setRecordingRemaining(Math.ceil(remaining));
        if (elapsed >= MAX_RECORD_SECONDS) {
          stopRecording();
        }
      }, 200);
    } catch (err) {
      logger.debug(err);
      setError("Mic permission denied or unavailable.");
    }
  }

  function stopRecording() {
    // Hide countdown immediately and prevent re-triggering controls while the recorder drains.
    setIsStoppingRecording(true);
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    recordingTimerRef.current = null;
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== "inactive") rec.stop();
  }

  async function unlock(e) {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    setError(null);
    setAuthError(null);
    setIsUnlocking(true);
    try {
      const next = (tokenInput || "").trim();
      if (!next) throw new Error("Enter a token");
      setToken(next);
      // probe
      const resp = await fetch(`${apiBaseUrl}/vocoder/files`, {
        headers: { Authorization: `Bearer ${next}` },
      });
      if (!resp.ok) throw new Error("Unauthorized (bad token)");
      setIsUnlocked(true);
      setTokenInput("");
      // Don't call refreshList() here: it uses apiFetch() which is derived from `token`.
      // React state updates are async; calling refresh immediately can use a stale token and re-lock.
      // The useEffect that depends on `isUnlocked` will fetch the list after state is committed.
    } catch (err) {
      logger.debug(err);
      setIsUnlocked(false);
      setAuthError(err.message || "Unauthorized");
    } finally {
      setIsUnlocking(false);
    }
  }

  return (
    <Container>
      <h1>Vocoder</h1>

      {!isUnlocked && (
        <Card style={{ marginBottom: 20 }}>
          <Card.Body>
            <Card.Title>Unlock</Card.Title>
            <Card.Text style={{ opacity: 0.8 }}>
              Enter the access token to enable vocoder controls.
            </Card.Text>

            <Form onSubmit={unlock}>
              <Form.Group controlId="vocoderToken" style={{ maxWidth: 620 }}>
                <Form.Label>Token</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="password"
                    value={tokenInput}
                    placeholder="Enter token…"
                    autoComplete="current-password"
                    onChange={(e) => setTokenInput(e.target.value)}
                    disabled={isUnlocking}
                  />
                  <InputGroup.Append>
                    <Button type="submit" variant="primary" disabled={isUnlocking}>
                      {isUnlocking ? "Unlocking…" : "Unlock"}
                    </Button>
                  </InputGroup.Append>
                </InputGroup>
              </Form.Group>
            </Form>

            {authError && (
              <Alert variant="danger" style={{ marginTop: 15, marginBottom: 0 }}>
                {authError}
              </Alert>
            )}
          </Card.Body>
        </Card>
      )}

      <Card style={{ marginBottom: 20, opacity: isUnlocked ? 1 : 0.5 }}>
        <Card.Body>
          <Card.Title>Input</Card.Title>

          <Row>
            <Col xs={12} md={6} style={{ marginBottom: 12 }}>
              <Form.File
                id="vocoderFile"
                label="Upload audio (wav/mp3)"
                custom
                disabled={!isUnlocked || isProcessing || isRecording}
                onChange={handleUploadFile}
              />
            </Col>

            <Col xs={12} md={6}>
              {!isRecording ? (
                <Button
                  block
                  variant="primary"
                  onClick={startRecording}
                  disabled={!isUnlocked || isProcessing || isStoppingRecording}
                >
                  Record (max {MAX_RECORD_SECONDS}s)
                </Button>
              ) : (
                <Button block variant="danger" onClick={stopRecording} disabled={!isUnlocked || isStoppingRecording}>
                  {isStoppingRecording ? "Stopping…" : `Stop recording (${recordingRemaining}s left)`}
                </Button>
              )}

              {isRecording && !isStoppingRecording && (
                <div style={{ marginTop: 10 }}>
                  <ProgressBar
                    now={((MAX_RECORD_SECONDS - recordingRemaining) / MAX_RECORD_SECONDS) * 100}
                    label={`${recordingRemaining}s`}
                  />
                </div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card style={{ marginBottom: 20, opacity: isUnlocked ? 1 : 0.5 }}>
        <Card.Body>
          <Card.Title>Processing</Card.Title>
          <ProgressBar
            now={isProcessing ? processingPct : 0}
            label={isProcessing ? `${processingPct}%` : "—"}
            animated={isProcessing}
            striped={isProcessing}
          />
          <div style={{ marginTop: 10, opacity: 0.8, fontSize: 12 }}>
            {isProcessing ? "Processing… (may take a bit on cold start)" : "Idle"}
          </div>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" style={{ marginBottom: 20 }}>
          {error}
        </Alert>
      )}

      {lastResult && (lastResult.originalUrl || lastResult.processedUrl) && (
        <Card style={{ marginBottom: 20 }}>
          <Card.Body>
            <Card.Title>Latest result</Card.Title>
            <Row>
              <Col xs={12} md={6} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>Original</div>
                <audio controls src={lastResult.originalUrl} style={{ width: "100%" }} />
                <div style={{ marginTop: 8 }}>
                  <a href={lastResult.originalUrl} download>
                    Download original
                  </a>
                </div>
              </Col>
              <Col xs={12} md={6}>
                <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>Vocoded</div>
                <audio controls src={lastResult.processedUrl} style={{ width: "100%" }} />
                <div style={{ marginTop: 8 }}>
                  <a href={lastResult.processedUrl} download>
                    Download vocoded
                  </a>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      <Card style={{ marginBottom: 30, opacity: isUnlocked ? 1 : 0.5 }}>
        <Card.Body>
          <Row className="align-items-center">
            <Col>
              <Card.Title style={{ marginBottom: 0 }}>Recent (last 30 minutes)</Card.Title>
              <div style={{ opacity: 0.7, fontSize: 12 }}>Auto-refreshes every 15s. Old files are deleted automatically.</div>
            </Col>
            <Col xs="auto">
              <Button variant="outline-primary" onClick={refreshList} disabled={!isUnlocked}>
                Refresh
              </Button>
            </Col>
            <Col xs="auto">
              <Button
                variant="outline-danger"
                onClick={cleanupOld}
                disabled={!isUnlocked || isCleaning}
              >
                {isCleaning ? "Deleting…" : "Delete old files"}
              </Button>
            </Col>
            <Col xs="auto">
              <Button
                variant="danger"
                onClick={() => setShowClearModal(true)}
                disabled={!isUnlocked || isCleaning || isClearing}
              >
                Delete ALL files
              </Button>
            </Col>
          </Row>

          {cleanupMsg && (
            <Alert variant="success" style={{ marginTop: 12 }}>
              {cleanupMsg}
            </Alert>
          )}

          <div style={{ marginTop: 15 }}>
            {files.length === 0 ? (
              <div style={{ opacity: 0.7 }}>No recent files.</div>
            ) : (
              files.map((f) => (
                <Card key={f.id || f.processedKey || f.originalKey} style={{ marginBottom: 12 }}>
                  <Card.Body>
                    <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 10 }}>
                      {new Date((f.lastModified || nowTs()) * 1000).toLocaleString()} —{" "}
                      <code>{f.id || f.processedKey || f.originalKey}</code>
                    </div>
                    <Row>
                      <Col xs={12} md={6} style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>Original</div>
                        <audio controls src={f.originalUrl} style={{ width: "100%" }} />
                        <div style={{ marginTop: 8 }}>
                          <a href={f.originalUrl} download>
                            Download original
                          </a>
                        </div>
                      </Col>
                      <Col xs={12} md={6}>
                        <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>Vocoded</div>
                        <audio controls src={f.processedUrl} style={{ width: "100%" }} />
                        <div style={{ marginTop: 8 }}>
                          <a href={f.processedUrl} download>
                            Download vocoded
                          </a>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))
            )}
          </div>
        </Card.Body>
      </Card>

      <Modal show={showClearModal} onHide={() => setShowClearModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete ALL vocoder files?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ marginBottom: 10 }}>
            This will permanently delete <b>all</b> files under <code>public/vocoder/</code>.
          </p>
          <Form.Group controlId="confirmDeleteAll">
            <Form.Label>
              Type <code>DELETE</code> to confirm
            </Form.Label>
            <Form.Control
              value={clearConfirm}
              onChange={(e) => setClearConfirm(e.target.value)}
              placeholder="DELETE"
              autoFocus
              disabled={isClearing}
            />
          </Form.Group>
          {error && <Alert variant="danger">{error}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowClearModal(false)} disabled={isClearing}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={clearAll}
            disabled={isClearing || clearConfirm.trim().toUpperCase() !== "DELETE"}
          >
            {isClearing ? "Deleting…" : "Delete all"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}


